import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'results-glossary',
    category: 'results',
    title: 'Glossaire — termes énergétiques en langage clair',
    summary: 'Chaque terme qui apparaît dans les graphiques de résultats et les cartes KPI, réuni en un seul endroit.',
    body: (
      <>
        <p>Les autres articles de cette section renvoient ici. Ajoutez-le à vos favoris comme référence rapide.</p>

        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Terme</th><th>Signification</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>kWh</strong> (kilowattheure)</td>
              <td>Une unité d'<em>énergie</em>. Un kWh ≈ un radiateur soufflant fonctionnant pendant une heure. Les factures sont en kWh.</td>
            </tr>
            <tr>
              <td><strong>kW</strong> (kilowatt)</td>
              <td>Une unité de <em>puissance</em> — la vitesse à laquelle l'énergie est consommée en ce moment. Une bouilloire de 2 kW pendant une demi-heure consomme 1 kWh.</td>
            </tr>
            <tr>
              <td><strong>Pic de demande</strong></td>
              <td>La puissance maximale (kW) soutirée sur une période. Les frais de réseau y sont souvent liés.</td>
            </tr>
            <tr>
              <td><strong>Production</strong></td>
              <td>Électricité produite par vos panneaux solaires, votre éolienne, etc.</td>
            </tr>
            <tr>
              <td><strong>Charge / consommation</strong></td>
              <td>Électricité utilisée par les bâtiments et les équipements.</td>
            </tr>
            <tr>
              <td><strong>Autoconsommation (%)</strong></td>
              <td>Sur toute l'énergie que <em>vous avez produite</em>, quelle part avez-vous utilisée vous-même
                (au lieu d'exporter). Élevée = vous gaspillez moins de votre production.</td>
            </tr>
            <tr>
              <td><strong>Autosuffisance (%)</strong></td>
              <td>Sur toute l'énergie que <em>vous avez consommée</em>, quelle part provenait de votre propre production.
                Élevée = vous dépendez moins du réseau.</td>
            </tr>
            <tr>
              <td><strong>Énergie partagée</strong> <em>(concept)</em></td>
              <td>Énergie produite par un membre de la communauté et consommée dans la même heure par
                un autre membre. De nombreux systèmes nationaux récompensent cela. La plateforme ne l'affiche pas
                actuellement comme un KPI séparé — il apparaît indirectement via l'autoconsommation et
                le graphique Bilan Énergétique.</td>
            </tr>
            <tr>
              <td><strong>Importation / exportation</strong></td>
              <td>Importation = électricité soutirée du réseau. Exportation = électricité injectée dans le réseau.</td>
            </tr>
            <tr>
              <td><strong>Tarif</strong></td>
              <td>Le prix de l'électricité, en €/kWh, souvent variable selon l'heure.</td>
            </tr>
            <tr>
              <td><strong>État de charge (SOC)</strong></td>
              <td>Le niveau de charge d'une batterie, de 0 % (vide) à 100 % (pleine).</td>
            </tr>
            <tr>
              <td><strong>NPV</strong> (Valeur actuelle nette)</td>
              <td>La valeur totale d'un investissement sur sa durée de vie, en monnaie d'aujourd'hui. <strong>Positive = rentable</strong> ; négative = perte.</td>
            </tr>
            <tr>
              <td><strong>IRR</strong> (Taux de rendement interne) <em>(concept)</em></td>
              <td>Le rendement annuel en pourcentage de l'investissement. Les résultats CBA de la plateforme
                affichent actuellement le NPV, le BCR, le remboursement, les économies annuelles et le CO₂ — pas l'IRR directement — mais
                le terme apparaît dans la description de l'outil.</td>
            </tr>
            <tr>
              <td><strong>BCR</strong> (Rapport bénéfices-coûts)</td>
              <td>Total des bénéfices divisé par le total des coûts. Au-dessus de 1 = l'investissement est rentabilisé.</td>
            </tr>
            <tr>
              <td><strong>Remboursement (années)</strong></td>
              <td>Combien d'années d'économies sont nécessaires pour couvrir le coût initial.</td>
            </tr>
            <tr>
              <td><strong>MAPE</strong> (Erreur absolue moyenne en pourcentage)</td>
              <td>Erreur de prévision. Plus faible = mieux — par ex. un MAPE de 10 % signifie que les prévisions s'écartent de
                10 % en moyenne.</td>
            </tr>
            <tr>
              <td><strong>GHI</strong></td>
              <td>Irradiance horizontale globale — total du rayonnement solaire frappant une surface horizontale, en W/m². Principale entrée météo pour les prévisions solaires.</td>
            </tr>
          </tbody>
        </Table>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-forecast',
    category: 'results',
    title: 'Lire le graphique de prévision',
    summary: 'Ce que signifient la ligne, la bande ombrée et le chiffre de précision.',
    body: (
      <>
        <p>
          Un résultat de prévision comporte trois parties : une <strong>ligne historique</strong>, une
          {' '}<strong>ligne prévue</strong>, et une <strong>bande ombrée</strong> autour de la
          prévision indiquant sa plage d'incertitude.
        </p>
        <ScreenshotPlaceholder
          img="forecast-results.png"
          description="A finished forecast — line for past data, a forecast line, and a shaded band around the prediction. A MAPE value and a Confidence level badge (LOW / MEDIUM / MEDIUM-HIGH / HIGH) are shown alongside."
          source="Platform — Forecasting page, after a successful run. Capture the result panel on the right: chart + the MAPE/Confidence indicators. (A community with uploaded load data is needed.)"
          caption="Un résultat de prévision typique."
        />

        <ul>
          <li><strong>Ligne historique</strong> — ce qui s'est réellement passé. Utilisez-la pour vérifier que les données d'entrée semblent correctes.</li>
          <li><strong>Ligne de prévision</strong> — la meilleure estimation unique du modèle pour l'avenir.</li>
          <li><strong>Bande ombrée</strong> — une plage dans laquelle la valeur réelle est susceptible de se situer. Une bande <em>étroite</em> signifie que le modèle est confiant ; une bande <em>large</em> indique une incertitude (souvent le cas sur de longs horizons ou avec peu de données d'entrée).</li>
        </ul>

        <h5 className="mt-3">Deux indicateurs de précision</h5>
        <p>
          La plateforme affiche deux indicateurs récapitulatifs à côté de chaque prévision — lisez-les ensemble :
        </p>

        <p className="mb-1"><strong>1. Niveau de confiance</strong> — un badge unique avec l'une des quatre valeurs suivantes :</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Badge</th><th>Signification</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>HIGH</strong></td><td>Beaucoup de données d'entrée propres ; résultats adaptés aux décisions opérationnelles.</td></tr>
            <tr><td><strong>MEDIUM-HIGH</strong></td><td>Bon. Utilisable pour la planification.</td></tr>
            <tr><td><strong>MEDIUM</strong></td><td>Données d'entrée exploitables mais imparfaites — vérifiez avant d'agir sur les cas limites.</td></tr>
            <tr><td><strong>LOW</strong></td><td>Données d'entrée limitées ou signal faible. Considérez la prévision comme indicative ; ajoutez plus d'historique.</td></tr>
          </tbody>
        </Table>

        <p className="mt-3 mb-1"><strong>2. MAPE</strong> (Erreur absolue moyenne en pourcentage) — l'écart moyen entre prévision et réalité, en %. Repères indicatifs du secteur :</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>MAPE</th><th>Signification</th></tr>
          </thead>
          <tbody>
            <tr><td>&lt; 10%</td><td>Très bon.</td></tr>
            <tr><td>10 – 20%</td><td>Bon. Utilisable pour la planification et l'optimisation.</td></tr>
            <tr><td>20 – 35%</td><td>Acceptable en phase initiale. Ajoutez des données ou des données météo pour améliorer.</td></tr>
            <tr><td>&gt; 35%</td><td>Considérez comme illustratif. Voir <ArticleLink to="tb-forecast-flat">« Ma prévision semble plate ou incorrecte »</ArticleLink>.</td></tr>
          </tbody>
        </Table>

        <Tip>
          Les prévisions solaires sont généralement plus précises que les prévisions de charge car le rayonnement solaire est plus
          prévisible. L'ajout d'un fichier météo fait encore baisser le MAPE solaire — voir
          {' '}<ArticleLink to="data-weather">Fichiers météo</ArticleLink>.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-optimisation-kpis',
    category: 'results',
    title: 'Lire les KPI d\'optimisation',
    summary: 'Ce que signifie chaque carte du Résumé KPI après une exécution d\'Optimisation.',
    body: (
      <>
        <p>
          Après une exécution d'optimisation, un <strong>Résumé KPI</strong> apparaît au-dessus des graphiques. Les
          cartes affichées dépendent de si votre communauté dispose d'une batterie et si un tarif était
          disponible, mais l'ensemble standard est présenté ci-dessous.
        </p>
        <ScreenshotPlaceholder
          img="kpi-cards.png"
          description="The KPI Summary panel that appears after running an optimisation — cards for Daily Cost, Self-Sufficiency, Self-Consumption, Grid Import, Grid Import Cost, Grid Export Revenue, Peak Import and CO2 Avoided."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run. Capture the KPI Summary row that appears above the charts."
          caption="Le panneau Résumé KPI issu du résultat d'optimisation."
        />

        <h5 className="mt-3">Chaque KPI expliqué</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>KPI</th><th>Ce qu'il indique</th><th>Comment l'améliorer</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Coût journalier</strong></td>
              <td>Coût total du fonctionnement de la communauté pour la journée optimisée (importations moins revenus d'exportation).</td>
              <td>De meilleurs signaux tarifaires, plus de PV, une batterie dimensionnée aux heures de pointe.</td>
            </tr>
            <tr>
              <td><strong>Autosuffisance (%)</strong></td>
              <td>« De l'énergie que j'ai consommée, quelle part provenait de ma propre production ? »</td>
              <td>Ajouter plus de PV ; réduire la consommation ; ajouter du stockage.</td>
            </tr>
            <tr>
              <td><strong>Autoconsommation (%)</strong></td>
              <td>« De l'énergie que j'ai produite, quelle part ai-je utilisée moi-même au lieu d'exporter ? »</td>
              <td>Ajouter une batterie ; décaler les charges flexibles vers les heures ensoleillées.</td>
            </tr>
            <tr>
              <td><strong>Importation depuis le réseau (kWh)</strong></td>
              <td>Énergie totale achetée au réseau ce jour-là.</td>
              <td>Plus de PV et de stockage ; charges flexibles aux heures solaires.</td>
            </tr>
            <tr>
              <td><strong>Coût d'importation réseau</strong></td>
              <td>Ce que les importations ont coûté en monnaie. Combine volume × tarif.</td>
              <td>Éviter les importations aux heures à tarif de pointe ; précharger depuis les heures moins chères.</td>
            </tr>
            <tr>
              <td><strong>Revenu d'export réseau</strong></td>
              <td>Ce que vous avez perçu pour les exportations.</td>
              <td>Dépend de votre schéma tarifaire d'exportation ; pas un objectif en soi si l'autoconsommation est privilégiée.</td>
            </tr>
            <tr>
              <td><strong>Pic d'importation (kW)</strong></td>
              <td>Soutirage horaire maximal du réseau pendant la journée. Les frais de réseau y sont souvent liés.</td>
              <td>Étaler les charges dans le temps ; utiliser une batterie pour l'écrêtage de pointe.</td>
            </tr>
            <tr>
              <td><strong>CO2 évité (kg)</strong></td>
              <td>Émissions évitées par rapport à la prise de la même énergie sur le réseau. Déterminé par le PV/éolien autoconsommé.</td>
              <td>Maximiser l'autoconsommation ; dimensionner le PV à la charge diurne.</td>
            </tr>
          </tbody>
        </Table>

        <h5 className="mt-3">Autoconsommation vs Autosuffisance — la paire la plus souvent confondue</h5>
        <p>Elles se ressemblent mais répondent à des questions opposées :</p>
        <ul>
          <li><strong>Autoconsommation</strong> concerne <em>votre production</em> : quelle part de ce que vous avez produit avez-vous utilisé ?</li>
          <li><strong>Autosuffisance</strong> concerne <em>votre consommation</em> : quelle part de ce que vous avez utilisé avez-vous produit vous-même ?</li>
        </ul>

        <h5 className="mt-3">Plages de vérification de cohérence</h5>
        <ul>
          <li>Une communauté avec seulement un modeste PV en toiture et sans batterie : autoconsommation 60–90 %, autosuffisance 10–25 %.</li>
          <li>Une communauté avec un grand parc solaire et une batterie : l'autosuffisance peut dépasser 50 %, l'autoconsommation 80–95 %.</li>
          <li>Une autosuffisance supérieure à ~90 % signifie que vous importez à peine — vérifiez que le fichier de charge n'est pas sous-estimé.</li>
        </ul>
        <Warn>
          Si un KPI semble irréaliste (par ex. 99 % d'autosuffisance avec seulement un modeste PV), voir
          {' '}<ArticleLink to="tb-kpi-sanity-check">« Mes KPI semblent trop bons ou trop mauvais »</ArticleLink>.
        </Warn>
      </>
    ),
  },
]
